import type {
  CapabilityBinding,
  DeployProjectionProfile,
  DeployTypeCode,
  DeployTypePluginHeadless,
  ModuleMeta,
  ZodSchema,
} from '../../../core/types';
import { assertPluginLayout } from '../../../platform/capability-registry/validatePluginLayout';
import { createPipelineKernel } from '../../../platform/pipeline-kernel';
import type { PipelineKernelInput } from '../../../platform/pipeline-kernel';

/** 交付插件 headless 统一工厂入参（全插件共用，不以 IaaS/MaaS 分叉） */
export interface DeployPluginHeadlessConfig<TSpec, TPayload extends Record<string, unknown>, TField extends string> {
  code: DeployTypeCode;
  fieldKeys: readonly TField[];
  capabilities: readonly CapabilityBinding[];
  modules: readonly ModuleMeta<TField>[];
  schema: ZodSchema<TSpec>;
  fromForm: PipelineKernelInput<TSpec, TPayload>['fromForm'];
  toForm: PipelineKernelInput<TSpec, TPayload>['toForm'];
  getInitialFormValues: PipelineKernelInput<TSpec, TPayload>['getInitialFormValues'];
  assertSubmitReady?: PipelineKernelInput<TSpec, TPayload>['assertSubmitReady'];
  projectionProfile: DeployProjectionProfile<TSpec, TPayload>;
  migrations: Record<number, (data: unknown) => TSpec>;
}

/**
 * 交付插件 headless 统一工厂：layout 校验 + pipeline-kernel。
 * 八插件均应经此入口；领域差异只体现在 config 内容。
 */
export function createDeployPluginHeadless<TSpec, TPayload extends Record<string, unknown>, TField extends string>(
  config: DeployPluginHeadlessConfig<TSpec, TPayload, TField>,
): DeployTypePluginHeadless<TSpec, TPayload> {
  assertPluginLayout({
    fieldKeys: config.fieldKeys,
    modules: config.modules,
    capabilities: config.capabilities,
  });

  const pipeline = createPipelineKernel({
    schema: config.schema,
    fromForm: config.fromForm,
    toForm: config.toForm,
    getInitialFormValues: config.getInitialFormValues,
    assertSubmitReady: config.assertSubmitReady,
    projectionProfile: config.projectionProfile,
    modules: config.modules,
  });

  return {
    code: config.code,
    modules: [...config.modules],
    capabilities: [...config.capabilities],
    pipeline,
    migrations: config.migrations,
  };
}
